import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Trans } from 'react-i18next';
import { ExternalLink, SentryErrorBox } from '~frontend-common/components/ui';
import { useTranslator } from '~frontend-common/i18n/translator';
import { Box } from '~ui-kit/components/box/box';
import { LoadingSpinner } from '~ui-kit/components/loading-spinner';
import { PageContentContainer } from '~web-portal/components/page-content-container';
import { PageHeader } from '~web-portal/components/page-header';
import { policyService } from '~web-portal/services';
import { useUserStore } from '~web-portal/stores';
import { Images } from '~web-portal/theme/images';
import { Links } from '~web-portal/variables/links';
import { ActivePolicies } from './components/active-policies';
import { Tabs, type Tab } from './components/tabs';

export function PoliciesPage() {
  const translator = useTranslator();
  const { tl } = translator;

  const tabs: Tab[] = [
    {
      id: 'active',
      label: tl('policyPages.activePolicies.tabs.activePolicies'),
    },
  ];

  const [selectedTab, setSelectedTab] = useState<Tab>(tabs[0]);
  const getFullName = useUserStore.use.getFullName();

  const policiesQuery = useQuery({
    queryKey: ['policies'],
    queryFn: () => policyService.getPolicies(),
  });

  const renderContent = () => {
    if (policiesQuery.isLoading) {
      return (
        <Box flexDirection="column" justifyContent="center" minHeight="35vh">
          <LoadingSpinner />
        </Box>
      );
    }

    if (policiesQuery.isError) {
      return <SentryErrorBox error={policiesQuery.error} translator={translator} my={3} />;
    }

    if (selectedTab?.id === 'active') {
      return <ActivePolicies policies={policiesQuery.data} />;
    }

    return null;
  };

  return (
    <Box data-id="policies-page" flexDirection="column" flex="1 1 auto">
      <PageContentContainer>
        <PageHeader
          icon={Images.policies.handWave}
          title={tl('policyPages.activePolicies.title', { fillers: [getFullName()] })}
          subtitle={
            <Trans
              i18n={translator.i18n}
              defaults={tl('policyPages.activePolicies.subtitle')}
              components={[
                <ExternalLink to={Links.pages[translator.getCurrentLanguageId()].helpCenter}>_</ExternalLink>,
              ]}
            />
          }
        />

        <Box mt={[4, 4, 5]} gridGap={3}>
          <Tabs tabs={tabs} selectedTab={selectedTab} onTabChange={setSelectedTab} />
        </Box>

        {renderContent()}
      </PageContentContainer>
    </Box>
  );
}
